import { UserRole } from "@fuse/recoil/pluginStudio/types"

import { ExamplePlugin } from "../types"

export const photoGalleryPlugin: ExamplePlugin = {
  id: "photo-gallery",
  name: "Photo Gallery",
  description: "Add photos for guests to view and relive the vibe.",
  category: "Media",
  tags: ["alpha", "photos", "gallery", "firebase", "upload"],
  difficulty: "advanced",
  preview: "Host uploads photos, guests can view a shared gallery.",
  iconName: "photos",
  showHostViewInDetails: true, // Show host view in event details
  requiresJoinToView: true, // Non-joined users cannot see photos
  files: {
    [UserRole.GUEST]: `import React, { useState, useEffect } from 'react';

export default function GuestView({ pluginApi, pluginComponents, eventMetadata, userRole, isLoggedIn }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [showGalleryModal, setShowGalleryModal] = useState(false);

  useEffect(() => {
    async function loadData() {
      try {
        const data = await pluginApi.getPluginEventInstanceData();
        setPhotos(data?.photos || []);
      } catch (error) {
        console.error('Error loading photos:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const handleImageClick = (photo) => {
    setSelectedPhoto(photo);
    setModalOpen(true);
  };

  const handleOpenGalleryModal = () => setShowGalleryModal(true);
  const handleCloseGalleryModal = () => setShowGalleryModal(false);
  const flyerImage = eventMetadata?.flyerImage || ""

  if (loading) {
    return (
      <pluginComponents.PluginContainer className="p-6">
        <div className="flex items-center justify-center py-8">
          <pluginComponents.LoadingSpinner size="lg" />
        </div>
      </pluginComponents.PluginContainer>
    );
  }

  return (
    <pluginComponents.PluginContainer className="p-0">
      <pluginComponents.ContentGrid cols={3} gap="md" className="!grid-cols-3">
        {photos.length === 0 ? (
          <pluginComponents.BaseCard className="col-span-full text-center text-white/70 mt-1 text-sm flex items-center justify-center !bg-transparent !border-transparent" padding="p-0">
            You'll see host - uploaded photos here
          </pluginComponents.BaseCard>
        ) : (
          photos.slice(0, 5).map((photo, idx) => (
            <pluginComponents.BaseCard key={photo.id} padding="p-0" className="overflow-hidden">
              <img
                src={photo.url}
                alt="Event photo"
                className="w-full h-28 object-cover rounded-lg cursor-pointer"
                onClick={() => handleImageClick(photo)}
              />
            </pluginComponents.BaseCard>
          ))
        )}
        {photos.length > 5 && (
          <pluginComponents.BaseCard
            className="relative flex items-center justify-center text-white text-xl font-bold w-full h-28 rounded-lg overflow-hidden cursor-pointer"
            padding="p-0"
            onClick={handleOpenGalleryModal}
          >
            <div
              className="absolute flex w-full h-full object-cover bg-cover bg-no-repeat overflow-hidden"
              style={{
                background: "url(" + photos[5].url + ")",
                backgroundPosition: "center",
              }}
            >
              <div className="bg-white/10 backdrop-blur-md flex items-center justify-center h-full w-full rounded-lg">
                <p className="z-10">+{photos.length - 5}</p>
              </div>
            </div>
          </pluginComponents.BaseCard>
        )}
      </pluginComponents.ContentGrid>
      <pluginComponents.ImageModal
        isOpen={modalOpen && !showGalleryModal}
        onClose={() => setModalOpen(false)}
        imageSrc={selectedPhoto?.url}
        timestamp={selectedPhoto?.timestamp}
        isHost={false}
      />
      <pluginComponents.PhotoGalleryModal
        isOpen={showGalleryModal}
        onClose={handleCloseGalleryModal}
        photos={photos}
        onPhotoClick={handleImageClick}
        flyerImage={flyerImage}
        pluginComponents={pluginComponents}
        selectedImage={selectedPhoto}
        showImageModal={modalOpen && showGalleryModal}
        onCloseImageModal={() => setModalOpen(false)}
        isHost={false}
        onDeleteImage={() => {}}
      />
      {/* No upload button for guests */}
    </pluginComponents.PluginContainer>
  );
}
`,
    [UserRole.HOST_EVENT_VIEW]: `import React, { useState, useEffect } from 'react';
export default function HostView({ pluginApi, pluginComponents, eventMetadata, userRole, isLoggedIn }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventId, setEventId] = useState("")
  const [pluginId, setPluginId] = useState("")
  const [userId, setUserId] = useState("")
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    async function loadData() {
      try {
        const data = await pluginApi.getPluginEventInstanceData();
        setPhotos(data?.photos || []);
        setEventId(data?.eventId || "")
        setPluginId(data?.pluginId || "")
        setUserId(data?.userId || "")
      } catch (error) {
        console.error('Error loading photos:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const handleFilesDrop = async (urls) => {
    if (!urls || urls.length === 0) return;
    const newPhotos = urls.map((downloadURL) => ({
      id: Date.now() + Math.random(),
      url: downloadURL,
      uploadedBy: userId,
      timestamp: new Date().toISOString(),
    }));
    const updatedPhotos = [...photos, ...newPhotos];
    setPhotos(updatedPhotos);
    await pluginApi.savePluginEventInstanceData({ photos: updatedPhotos });
  };

  const handleOpenGalleryModal = () => setShowGalleryModal(true);
  const handleCloseGalleryModal = () => setShowGalleryModal(false);
  const handleOpenImageModal = (photo) => {
    setSelectedImage(photo);
    setShowImageModal(true);
  };
  const handleCloseImageModal = () => setShowImageModal(false);

  const handleDeleteImage = async (photo) => {
    const updatedPhotos = photos.filter((p) => p.url !== photo.url);
    setPhotos(updatedPhotos);
    await pluginApi.savePluginEventInstanceData({ photos: updatedPhotos });
    setShowImageModal(false);
  };

  const flyerImage = eventMetadata?.flyerImage || ""

  if (loading) {
    return (
      <pluginComponents.PluginContainer className="p-6">
        <div className="flex items-center justify-center py-8">
          <pluginComponents.LoadingSpinner size="lg" />
        </div>
      </pluginComponents.PluginContainer>
    );
  }

  return (
    <pluginComponents.PluginContainer className="">
      <pluginComponents.ContentGrid cols={3} gap="md" className="!grid-cols-3">
        {photos.length === 0 ? (
          <pluginComponents.BaseCard
            className="col-span-full text-center text-white/60 min-h-[180px] flex items-center justify-center flex-col gap-4 !bg-transparent !border-transparent"
            padding="lg"
          >
            <pluginComponents.PluginImage
              imageKey={"imgPhotoGallery"}
              alt="mySvgImage"
            />
            Only you can add images — share them with your guests.
          </pluginComponents.BaseCard>
        ) : (
          photos.slice(0, 5).map((photo, idx) => (
            <pluginComponents.BaseCard
              key={photo.id}
              padding="p-0"
              className="overflow-hidden"
            >
              <img
                src={photo.url}
                alt="Event photo"
                className="w-full h-28 object-cover rounded-lg cursor-pointer"
                onClick={() => handleOpenImageModal(photo)}
              />
            </pluginComponents.BaseCard>
          ))
        )}
        {photos.length > 5 && (
          <pluginComponents.BaseCard
            className="relative flex items-center justify-center text-white text-xl font-bold w-full h-28 rounded-lg overflow-hidden cursor-pointer"
            padding="p-0"
            onClick={handleOpenGalleryModal}
          >
            <div
              className="absolute flex w-full h-full object-cover bg-cover bg-no-repeat"
              style={{
                background: "url(" + photos[5].url + ")",
                backgroundPosition: "center",
              }}
            >
              <div className="bg-white/10 backdrop-blur-md rounded-lg flex items-center justify-center h-full w-full">
                <p className="z-10">+{photos.length - 5}</p>
              </div>
            </div>
          </pluginComponents.BaseCard>
        )}
      </pluginComponents.ContentGrid>
      <pluginComponents.PhotoGalleryModal
        isOpen={showGalleryModal}
        onClose={handleCloseGalleryModal}
        photos={photos}
        onPhotoClick={handleOpenImageModal}
        onFilesDrop={handleFilesDrop}
        userId={userId}
        eventId={eventId}
        pluginId={pluginId}
        flyerImage={flyerImage}
        pluginComponents={pluginComponents}
        selectedImage={selectedImage}
        showImageModal={showImageModal && showGalleryModal}
        onCloseImageModal={handleCloseImageModal}
        isHost={true}
        multiple={true}
        onDeleteImage={handleDeleteImage}
      />
      <pluginComponents.ImageModal
        isOpen={showImageModal && !showGalleryModal}
        onClose={handleCloseImageModal}
        imageSrc={selectedImage?.url}
        timestamp={selectedImage?.timestamp}
        isHost={true}
        onDelete={() => handleDeleteImage(selectedImage)}
      />
       <div className={photos.length > 0 ? "mt-6" : ""}>
        <pluginComponents.FileDropzone
          accept="image/*"
          multiple={true}
          userId={userId}
          eventId={eventId}
          pluginId={pluginId}
          onFilesDrop={handleFilesDrop}
          className=""
        >
          <div className="flex flex-row items-center justify-center gap-2">
            <pluginComponents.PluginImage
              imageKey={"imgCameraAdd"}
              alt="mySvgImage"
            />
            <p className="text-white/70 text-base text-end mt-1">
              Click here to upload
            </p>
          </div>
        </pluginComponents.FileDropzone>
      </div>
    </pluginComponents.PluginContainer>
  );
}
`,
    [UserRole.HOST_CONFIG_VIEW]: `import React from 'react';
export default function HostConfigView() {
  // TODO: Add configuration options if needed
  return (
    <div className="p-6 bg-white rounded-lg shadow-md max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">Alpha Photo Gallery Configuration</h2>
      <div className="text-gray-700">No configuration options available yet.</div>
    </div>
  );
}
`,
  },
}
